import React, { useState } from 'react';
import { gql } from '@apollo/client';
import { useQuery } from '@apollo/client/react';
import _ from 'lodash';
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Divider,
  Grid,
  IconButton,
  InputAdornment,
  MenuItem,
  Paper,
  Select,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import AddIcon from '@mui/icons-material/Add';
import DirectionsRunIcon from '@mui/icons-material/DirectionsRun';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import FitnessCenterIcon from '@mui/icons-material/FitnessCenter';
import HotelIcon from '@mui/icons-material/Hotel';
import SearchIcon from '@mui/icons-material/Search';
import TrainingPlanWorkoutListItem from './TrainingPlanWorkoutListItem';
import TrainingPlanScheduleDaySquare from './TrainingPlanScheduleDaySquare';
import Loading from '../shared/Loading';
import ServerError from '../error/ServerError';

const MY_CREATED_WORKOUTS_QUERY = gql`
  query GetMyCreatedWorkoutsQuery($input: MyCreatedWorkoutsQueryInput!) {
    myCreatedWorkouts(input: $input) {
      _id
      title
      coverImageUrl
    }
  }
`;

const createEventFromWorkout = function (workout, day) {
  return {
    objectId: workout.objectId ?? workout._id,
    eventType: 0,
    day: day,
  };
};

const WEEKDAYS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
const WEEK_ACCENTS = ['#27ae60', '#2f80ed', '#8b5cf6', '#f5a400'];
const WEEK_TAGS = ['Foundation', 'Build', 'Intensify', 'Deload'];

const dayType = (events) => {
  if (events.length > 0) {
    const title = events[0].title ?? '';
    if (/conditioning|cardio/i.test(title)) {
      return 'conditioning';
    }
    if (/recovery|mobility/i.test(title)) {
      return 'recovery';
    }
    return 'workout';
  }

  return 'rest';
};

const eventIcon = (type, size = 24) => {
  const shared = {
    width: size,
    height: size,
    color: '#fff',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: '50%',
    flex: '0 0 auto',
  };

  if (type === 'conditioning') {
    return (
      <Box sx={{ ...shared, backgroundColor: '#f97316' }}>
        <DirectionsRunIcon sx={{ fontSize: size * 0.62 }} />
      </Box>
    );
  }

  if (type === 'recovery') {
    return (
      <Box sx={{ ...shared, backgroundColor: '#4f94ff' }}>
        <AccessTimeIcon sx={{ fontSize: size * 0.62 }} />
      </Box>
    );
  }

  if (type === 'rest') {
    return (
      <Box sx={{ ...shared, backgroundColor: '#e8edf5', color: '#63758d' }}>
        <HotelIcon sx={{ fontSize: size * 0.62 }} />
      </Box>
    );
  }

  return (
    <Box sx={{ ...shared, backgroundColor: '#7357ee' }}>
      <FitnessCenterIcon sx={{ fontSize: size * 0.62 }} />
    </Box>
  );
};

const Stat = ({ value, label, icon }) => (
  <Box
    sx={{
      minWidth: 122,
      px: 3,
      py: 1,
      borderRight: '1px solid #dde5ef',
      '&:last-of-type': { borderRight: 0 },
    }}
  >
    <Stack direction="row" spacing={1.25} alignItems="center">
      {icon}
      <Box>
        <Typography sx={{ fontWeight: 800, fontSize: 28, lineHeight: 1 }}>
          {value}
        </Typography>
        <Typography sx={{ color: '#506176', fontSize: 13 }}>{label}</Typography>
      </Box>
    </Stack>
  </Box>
);

const WeekHeader = ({ weekNumber, events }) => {
  const workouts = events.filter((event) => dayType([event]) === 'workout');
  const restDays = 7 - new Set(events.map((event) => event.day)).size;
  const accent = WEEK_ACCENTS[(weekNumber - 1) % WEEK_ACCENTS.length];

  return (
    <Stack
      direction="row"
      alignItems="center"
      justifyContent="space-between"
      sx={{ px: 2, py: 1.5, borderBottom: '1px solid #dde5ef' }}
    >
      <Stack direction="row" spacing={1.5} alignItems="center">
        <Box
          sx={{
            width: 28,
            height: 28,
            borderRadius: 2,
            color: '#fff',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontWeight: 800,
            backgroundColor: accent,
          }}
        >
          {weekNumber}
        </Box>
        <Typography sx={{ fontWeight: 800, fontSize: 16 }}>
          Week {weekNumber}
        </Typography>
        <Chip
          size="small"
          label={WEEK_TAGS[(weekNumber - 1) % WEEK_TAGS.length]}
          sx={{
            height: 24,
            color: accent,
            fontWeight: 700,
            backgroundColor: `${accent}1f`,
          }}
        />
      </Stack>
      <Stack direction="row" spacing={2} alignItems="center">
        <Typography sx={{ color: '#33445a', fontSize: 13 }}>
          {workouts.length} Workouts
        </Typography>
        <Typography sx={{ color: '#0f1d2d' }}>•</Typography>
        <Typography sx={{ color: '#33445a', fontSize: 13 }}>
          {restDays} Rest
        </Typography>
      </Stack>
    </Stack>
  );
};

const WorkoutLibrary = ({ onSelect }) => {
  const [query, setQuery] = useState('');
  const { data, loading, error } = useQuery(MY_CREATED_WORKOUTS_QUERY, {
    variables: { input: { query, limit: 30 } },
  });
  const workouts = data?.myCreatedWorkouts ?? [];

  return (
    <Paper
      variant="outlined"
      sx={{ borderColor: '#dbe3ee', borderRadius: 1, overflow: 'hidden' }}
    >
      <Box sx={{ p: 2 }}>
        <Typography sx={{ fontWeight: 800, fontSize: 18, mb: 2 }}>
          Workout Library
        </Typography>
        <Stack direction="row" spacing={1.25} sx={{ mb: 2 }}>
          <TextField
            size="small"
            fullWidth
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            placeholder="Search workouts..."
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ color: '#62748b', fontSize: 20 }} />
                </InputAdornment>
              ),
            }}
          />
          <Select size="small" value="all" sx={{ minWidth: 132 }}>
            <MenuItem value="all">All Categories</MenuItem>
          </Select>
        </Stack>
        {!!loading && <Loading circular />}
        {!!error && <ServerError message={error.message} />}
        <Stack spacing={1}>
          {workouts.map((workout) => {
            const type = dayType([workout]);
            return (
              <Box
                key={workout._id}
                sx={{
                  border: '1px solid #dbe3ee',
                  borderRadius: 1,
                  backgroundColor: '#fff',
                  overflow: 'hidden',
                }}
              >
                <TrainingPlanWorkoutListItem workout={workout} variant="library">
                  <Stack
                    direction="row"
                    alignItems="center"
                    spacing={1.25}
                    sx={{ p: 1.25 }}
                  >
                    <DragIndicatorIcon sx={{ color: '#4f6075', fontSize: 18 }} />
                    {eventIcon(type, 28)}
                    <Box sx={{ minWidth: 0, flex: 1 }}>
                      <Typography noWrap sx={{ fontWeight: 700, fontSize: 14 }}>
                        {workout.title}
                      </Typography>
                      <Typography sx={{ color: '#506176', fontSize: 12 }}>
                        {type === 'conditioning'
                          ? '30 min • Cardio'
                          : type === 'recovery'
                          ? '20 min • Mobility'
                          : '45 min • 8 exercises'}
                      </Typography>
                    </Box>
                    <IconButton
                      size="small"
                      onClick={(event) => {
                        event.preventDefault();
                        event.stopPropagation();
                        onSelect(workout);
                      }}
                      sx={{
                        border: '1px solid #cfd8e5',
                        borderRadius: 1,
                        width: 32,
                        height: 32,
                      }}
                    >
                      <AddIcon fontSize="small" />
                    </IconButton>
                  </Stack>
                </TrainingPlanWorkoutListItem>
              </Box>
            );
          })}
        </Stack>
      </Box>
    </Paper>
  );
};

const TrainingPlanScheduleEditor = ({ trainingPlan, onSave }) => {
  const [selectedDay, setSelectedDay] = useState(1);

  const handleWorkoutSelected = (workout) => {
    handleAdd(workout, selectedDay);
  };

  const handleOpenAddWorkoutClick = (event, day) => {
    event.preventDefault();
    setSelectedDay(day);
  };

  const handleRemove = (event) => {
    let changes = { ...trainingPlan };
    changes.events = (trainingPlan.events || []).filter(
      (e) => event._id !== e._id
    );
    onSave(changes);
  };
  const handleAdd = (workout, day = selectedDay) => {
    let changes = { ...trainingPlan };
    const event = createEventFromWorkout(workout, day);

    if (changes.events && changes.events.length > 0) {
      changes.events = [...changes.events, event];
    } else {
      changes.events = [event];
    }
    onSave(changes);
  };
  const handleMove = (workout, day) => {
    const changes = _.cloneDeep(trainingPlan);
    changes.events = changes.events ?? [];
    changes.events = changes.events.map((i) => {
      if (i._id === workout._id) {
        return {
          ...i,
          day,
        };
      }
      return i;
    });
    onSave(changes);
  };

  const events = trainingPlan && trainingPlan.events ? trainingPlan.events : [];
  const toPositiveInt = (value, fallback = 0) => {
    const parsed = parseInt(value, 10);
    return Number.isFinite(parsed) && parsed > 0 ? parsed : fallback;
  };
  const maxEventDay = Math.max(
    1,
    ...events.map((event) => toPositiveInt(event.day, 1))
  );
  const maxStoredWeek = Math.max(
    0,
    ...(trainingPlan.weeks || []).map((week) =>
      toPositiveInt(week.weekNumber ?? week.week, 0)
    )
  );
  const weekCount = Math.max(4, Math.ceil(maxEventDay / 7), maxStoredWeek);
  const avgDuration = events.length > 0 ? 42 : 0;
  const restDayCount =
    weekCount * 7 - new Set(events.map((event) => event.day)).size;
  const weeks = Array.from({ length: weekCount }, (_, index) => index + 1);
  const handleAddWeek = () => {
    const existingWeeks = trainingPlan.weeks || [];
    const nextWeekNumber = weekCount + 1;
    const changes = {
      ...trainingPlan,
      weeks: [
        ...existingWeeks,
        {
          weekNumber: nextWeekNumber,
          title: `Week ${nextWeekNumber}`,
          desc: '',
        },
      ],
    };

    onSave(changes);
  };

  return (
    <Box>
      <Stack
        direction={{ xs: 'column', lg: 'row' }}
        justifyContent="space-between"
        alignItems={{ xs: 'stretch', lg: 'center' }}
        spacing={2}
        sx={{ mb: 2 }}
      >
        <Paper
          variant="outlined"
          sx={{
            display: 'flex',
            flexWrap: 'wrap',
            borderRadius: 1,
            borderColor: '#dbe3ee',
            overflow: 'hidden',
          }}
        >
          <Stat value={weekCount} label="Weeks" />
          <Stat value={events.length} label="Workouts" />
          <Stat value={restDayCount} label="Rest Days" />
          <Stat
            value={`${avgDuration} min`}
            label="Avg. Workout"
            icon={<AccessTimeIcon sx={{ color: '#5e7f91' }} />}
          />
        </Paper>
      </Stack>

      <Grid container spacing={3}>
        <Grid item xs={12} lg={9}>
          <Stack spacing={2}>
            {weeks.map((weekNumber) => {
              const weekStart = (weekNumber - 1) * 7 + 1;
              const weekEvents = events.filter(
                (event) => event.day >= weekStart && event.day < weekStart + 7
              );
              const accent = WEEK_ACCENTS[(weekNumber - 1) % WEEK_ACCENTS.length];

              return (
                <Card
                  key={weekNumber}
                  variant="outlined"
                  sx={{
                    borderRadius: 1,
                    borderColor: '#dbe3ee',
                    borderLeft: `4px solid ${accent}`,
                    boxShadow: 'none',
                  }}
                >
                  <WeekHeader weekNumber={weekNumber} events={weekEvents} />
                  <CardContent sx={{ p: 0, '&:last-child': { pb: 0 } }}>
                    <Box
                      sx={{
                        display: 'grid',
                        gridTemplateColumns: {
                          xs: '1fr',
                          sm: 'repeat(2, 1fr)',
                          md: 'repeat(4, 1fr)',
                          xl: 'repeat(7, minmax(0, 1fr))',
                        },
                      }}
                    >
                      {WEEKDAYS.map((weekday, index) => {
                        const day = weekStart + index;
                        const dayEvents = events.filter((event) => event.day === day);
                        return (
                          <Box
                            key={day}
                            onClick={() => setSelectedDay(day)}
                            sx={{
                              minHeight: 128,
                              borderRight: {
                                xs: 0,
                                xl: index === WEEKDAYS.length - 1 ? 0 : '1px solid #dbe3ee',
                              },
                              borderBottom: {
                                xs: '1px solid #dbe3ee',
                                xl: 0,
                              },
                              outline:
                                selectedDay === day
                                  ? '2px solid rgba(79, 148, 255, 0.35)'
                                  : 'none',
                              outlineOffset: '-2px',
                            }}
                          >
                            <TrainingPlanScheduleDaySquare
                              key={day}
                              day={day}
                              weekday={weekday}
                              trainingPlan={trainingPlan}
                              events={dayEvents}
                              onRemove={handleRemove}
                              onAdd={handleAdd}
                              onOpenAddWorkout={(event) => {
                                handleOpenAddWorkoutClick(event, day);
                              }}
                              onMove={handleMove}
                            />
                          </Box>
                        );
                      })}
                    </Box>
                  </CardContent>
                </Card>
              );
            })}
            <Button
              onClick={handleAddWeek}
              variant="outlined"
              startIcon={<AddIcon />}
              sx={{ borderStyle: 'dashed', py: 1.25 }}
            >
              Add Week
            </Button>
          </Stack>
        </Grid>
        <Grid item xs={12} lg={3}>
          <Stack spacing={2}>
            <WorkoutLibrary onSelect={handleWorkoutSelected} />
            <Paper variant="outlined" sx={{ p: 2, borderRadius: 1, borderColor: '#dbe3ee' }}>
              <Typography sx={{ fontWeight: 800, fontSize: 16, mb: 1.5 }}>
                Tips & Shortcuts
              </Typography>
              <Stack spacing={1.5}>
                <Typography sx={{ color: '#506176', fontSize: 13 }}>
                  Drag workouts from the library to add them to any day.
                </Typography>
                <Typography sx={{ color: '#506176', fontSize: 13 }}>
                  Click a day to set where the library add button places workouts.
                </Typography>
                <Typography sx={{ color: '#506176', fontSize: 13 }}>
                  Use Duplicate Week to quickly copy structure and workouts.
                </Typography>
              </Stack>
            </Paper>
            <Paper variant="outlined" sx={{ p: 2, borderRadius: 1, borderColor: '#dbe3ee' }}>
              <Typography sx={{ fontWeight: 800, fontSize: 16 }}>
                Program Preview
              </Typography>
              <Divider sx={{ my: 1.5 }} />
              <Typography sx={{ color: '#506176', fontSize: 13 }}>
                See how this plan will look for your athletes.
              </Typography>
            </Paper>
          </Stack>
        </Grid>
      </Grid>
    </Box>
  );
};

export default TrainingPlanScheduleEditor;
